/**
 * The portal's own page for each thing that has one — an opening, an
 * employer, a position report — and the one spelling of its address
 * (Issue 570).
 *
 * One spelling because two of them on a page sent the App Router's prefetch
 * of one round forever (Issue 567), and because a hand-built `/jobs/${key}`
 * breaks on a key with `#`, `?`, `/` or `%` in it. Here in lib/ so that
 * everything that links to a page can reach it: the pages and components,
 * and what lib itself sends — a chat tool's `page`, an action's `href`.
 * tests/guards.test.ts holds every other file to it.
 *
 * The id is one path segment, `encodeURIComponent`d; the page decodes it
 * once (Next 16 hands a dynamic segment over still encoded). Which of these
 * addresses open as a card over the page, and how, is
 * `components/shell/cards.ts`. The pushes' links are Python's
 * (`pipeline/notify/links.py`), to the same pages by the same rule.
 *
 * Imports nothing: the browser runs it.
 */

/** Each thing's own page, by its first path segment. */
export const PAGES = { job: "jobs", company: "companies", report: "reports" } as const;
export type PageKind = keyof typeof PAGES;

/** The address of that thing's own page. */
export function pageHref(kind: PageKind, id: string): string {
  return `/${PAGES[kind]}/${encodeURIComponent(id)}`;
}

/** An opening's own page. */
export const jobHref = (key: string) => pageHref("job", key);
/** An employer's own page. */
export const companyHref = (slug: string) => pageHref("company", slug);
/** A position report's own page. */
export const reportHref = (id: string | number) => pageHref("report", String(id));
